import api from './api';

export type PointsTransactionType = 'Earn' | 'Redeem' | 'Expire' | 'Void';
export type MoneyMovementType = 'TopUp' | 'Spend' | 'Void';

/**
 * Whether a history row is denominated in Points ("saldo" / pts) or
 * StoredValue money ("saldo en €"). The backend money-movement ledger
 * (TopUp/Spend/Void) is a distinct currency from Points.
 */
export type MovementCurrency = 'points' | 'money';

export interface Transaction {
    id: string;
    brandName: string;
    date: string;
    amount: number;
    transactionType: PointsTransactionType | MoneyMovementType;
    /**
     * Currency of the movement. Honoured from the backend when present;
     * otherwise inferred (TopUp/Spend are always money movements).
     */
    currency?: MovementCurrency;
    detail?: string;
}

export interface WalletBalance {
    brandId: string;
    balance: number;
}

/**
 * A User's StoredValue ("saldo en €") for one Brand: real money plus the
 * non-refundable promotional bonus bucket, and their sum.
 */
export interface PurseBalance {
    realBalance: number;
    bonusBalance: number;
    totalBalance: number;
}

/**
 * Result of confirming a Spend: the updated Purse balances plus the Points earned
 * on the amount spent and the new Points balance for that Brand's Wallet.
 */
export interface SpendResult extends PurseBalance {
    pointsEarned: number;
    newPointsBalance: number;
}

export interface OtpResponse {
    otpCode: string;
    expiresInSeconds: number;
}

/**
 * The signed Redsys redirect envelope returned by `create-payment`. The app POSTs
 * `signatureVersion` / `merchantParameters` / `signature` (as `Ds_*` form fields) to
 * `redsysUrl`, sending the User to the Brand's hosted TPV Virtual page. `order` is the
 * `Ds_Order` for reference/reconciliation. The balance is credited by the backend's
 * server-to-server notification, never by this redirect or the browser return.
 */
export interface TopUpPaymentEnvelope {
    redsysUrl: string;
    signatureVersion: string;
    merchantParameters: string;
    signature: string;
    order: string;
}

export interface Reward {
    id: string;
    name: string;
    description?: string;
    pointsCost: number;
}

/**
 * Infer a movement's currency from its type when the backend doesn't say.
 * TopUp/Spend are exclusive to the StoredValue ledger, so they are money;
 * everything else (Earn/Redeem/Expire, and points Void) is points.
 */
function inferCurrency(type: Transaction['transactionType']): MovementCurrency {
    return type === 'TopUp' || type === 'Spend' ? 'money' : 'points';
}

export const walletService = {
    getBalance: async (): Promise<WalletBalance[]> => {
        try {
            const response = await api.get('/api/points/balance');
            const data = response.data;

            // Handle both direct array and wrapped responses
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.$values)) return data.$values;
            if (data && Array.isArray(data.wallets)) return data.wallets;
            if (data && Array.isArray(data.balances)) return data.balances;
            if (data && Array.isArray(data.data)) return data.data;

            console.warn('Unexpected balance response shape:', data);
            return [];
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al obtener la cartera. Por favor, inténtalo de nuevo.');
        }
    },

    getPurseBalance: async (brandId: string): Promise<PurseBalance> => {
        try {
            const response = await api.get('/api/purse/balance', { params: { brandId } });
            const data = response.data;

            return {
                realBalance: data?.realBalance ?? 0,
                bonusBalance: data?.bonusBalance ?? 0,
                totalBalance: data?.totalBalance ?? 0,
            };
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al obtener el saldo en €. Por favor, inténtalo de nuevo.');
        }
    },

    getBrandRewards: async (brandId: string): Promise<Reward[]> => {
        try {
            const response = await api.get(`/api/rewards/catalog?brandId=${brandId}`);
            const data = response.data;

            // Handle wrapped responses
            if (Array.isArray(data)) return data;
            if (data && Array.isArray(data.$values)) return data.$values;
            if (data && Array.isArray(data.rewards)) return data.rewards;
            if (data && Array.isArray(data.data)) return data.data;

            console.warn('Unexpected rewards response shape:', data);
            return [];
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al obtener los premios.');
        }
    },

    generateOtp: async (rewardId?: string): Promise<OtpResponse> => {
        try {
            const response = await api.post('/api/otp/generate', {
                rewardId: rewardId ?? null,
            });
            const data = response.data;

            return {
                otpCode: data.otpCode ?? data.code ?? data.otp ?? '',
                expiresInSeconds: data.expiresInSeconds ?? data.expiresIn ?? data.ttl ?? 300,
            };
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al generar el código. Por favor, inténtalo de nuevo.');
        }
    },

    getPurseMovements: async (): Promise<Transaction[]> => {
        try {
            const response = await api.get('/api/purse/movements');
            const data = response.data;

            let rows: Transaction[] = [];
            if (Array.isArray(data)) rows = data;
            else if (data && Array.isArray(data.$values)) rows = data.$values;
            else if (data && Array.isArray(data.movements)) rows = data.movements;
            else if (data && Array.isArray(data.data)) rows = data.data;
            else console.warn('Unexpected purse movements response shape:', data);

            // Backend tags each row currency: "money"; default it defensively.
            return rows.map((tx) => ({
                ...tx,
                currency: tx.currency ?? 'money',
            }));
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al obtener los movimientos de saldo en €.');
        }
    },

    getUserHistory: async (): Promise<Transaction[]> => {
        // StoredValue money movements are non-fatal: a failure there must not
        // sink the points history, which is the primary feed.
        const moneyPromise = walletService.getPurseMovements().catch((err) => {
            console.error('Error fetching purse movements:', err);
            return [] as Transaction[];
        });

        try {
            const response = await api.get('/api/points/history');
            const data = response.data;

            let rows: Transaction[] = [];
            if (Array.isArray(data)) rows = data;
            else if (data && Array.isArray(data.$values)) rows = data.$values;
            else if (data && Array.isArray(data.transactions)) rows = data.transactions;
            else if (data && Array.isArray(data.history)) rows = data.history;
            else if (data && Array.isArray(data.data)) rows = data.data;
            else console.warn('Unexpected history response shape:', data);

            // Tag each row's currency so the UI can render € for money movements
            // (TopUp/Spend are always StoredValue) and pts for points movements.
            // A backend-provided `currency` always wins if present.
            const points = rows.map((tx) => ({
                ...tx,
                currency: tx.currency ?? inferCurrency(tx.transactionType),
            }));

            // Merge both ledgers; the History page sorts them chronologically.
            const money = await moneyPromise;
            return [...points, ...money];
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al obtener el historial.');
        }
    },

    scanQr: async (code: string): Promise<{ pointsEarned: number; newBalance: number }> => {
        try {
            const response = await api.post('/api/points/scan-qr', { code });
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al escanear el código QR. Puede que haya expirado.');
        }
    },

    /**
     * Claim a top-up QR: credits StoredValue to the User's Purse and returns the
     * updated balances. A top-up only adds money, so it needs no pre-confirmation.
     */
    claimTopUp: async (code: string): Promise<PurseBalance> => {
        try {
            const response = await api.post('/api/purse/topup/claim', { code });
            const data = response.data;
            return {
                realBalance: data?.realBalance ?? 0,
                bonusBalance: data?.bonusBalance ?? 0,
                totalBalance: data?.totalBalance ?? 0,
            };
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al procesar la recarga. El código puede haber expirado.');
        }
    },

    /**
     * Confirm a charge (Spend) QR: debits the Purse (bonus-first) and earns Points
     * on the amount spent. Only ever called after the User authorizes the amount on
     * the pre-charge confirmation screen (ADR-0007). "Saldo insuficiente" (backend
     * 409 / INSUFFICIENT_STORED_VALUE) is surfaced as a clear, blocking message with
     * no partial payment.
     */
    confirmSpend: async (code: string): Promise<SpendResult> => {
        try {
            const response = await api.post('/api/purse/spend/confirm', { code });
            const data = response.data;
            return {
                realBalance: data?.realBalance ?? 0,
                bonusBalance: data?.bonusBalance ?? 0,
                totalBalance: data?.totalBalance ?? 0,
                pointsEarned: data?.pointsEarned ?? 0,
                newPointsBalance: data?.newPointsBalance ?? 0,
            };
        } catch (error: any) {
            if (error.response?.data?.errorCode === 'INSUFFICIENT_STORED_VALUE') {
                throw new Error('No tienes saldo suficiente. Recarga o usa otro medio de pago.');
            }
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            throw new Error('Error al confirmar el pago. El código puede haber expirado.');
        }
    },

    /**
     * Initiate an online TopUp: asks the backend to build + sign a Redsys payment for
     * `amount` € against `brandId`, returning the redirect envelope the app POSTs to the
     * hosted TPV Virtual page. No money moves here — the Purse is credited only by the
     * authoritative server-to-server notification once the User pays (ADR-0009). The
     * backend rejects Brands that aren't StoredValue + Redsys-configured.
     */
    createTopUpPayment: async (brandId: string, amount: number): Promise<TopUpPaymentEnvelope> => {
        try {
            const response = await api.post('/api/purse/topup/create-payment', { brandId, amount });
            const data = response.data;
            // The API serializes with the web camelCase policy, which keeps the `Ds_`
            // prefix's underscore (e.g. `ds_MerchantParameters`); accept the raw Redsys
            // casing too so a serialization tweak can't silently break the redirect.
            return {
                redsysUrl: data?.redsysUrl ?? data?.RedsysUrl ?? '',
                signatureVersion: data?.ds_SignatureVersion ?? data?.Ds_SignatureVersion ?? '',
                merchantParameters: data?.ds_MerchantParameters ?? data?.Ds_MerchantParameters ?? '',
                signature: data?.ds_Signature ?? data?.Ds_Signature ?? '',
                order: data?.order ?? data?.Order ?? '',
            };
        } catch (error: any) {
            if (error.response?.data?.detail) {
                throw new Error(error.response.data.detail);
            }
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            throw new Error('No se pudo iniciar la recarga online. Por favor, inténtalo de nuevo.');
        }
    },
};
